import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes as DomRoutes } from 'react-router-dom';

import { Loader } from '@/components/Ui/Loader';
import auth from '@/helpers/auth';
import ConfirmLoginPage from '@/pages/Auth/ConfirmLoginPage';
import Google2faPage from '@/pages/Auth/Google2faPage';
import LoginPage from '@/pages/Auth/LoginPage';
import RefreshSessionPage from '@/pages/Auth/RefreshSessionPage';
import RegisterPage from '@/pages/Auth/RegisterPage';
import RequestPasswordResetPage from '@/pages/Auth/RequestPasswordResetPage';
import ResetPasswordPage from '@/pages/Auth/ResetPasswordPage';

const AboutPage = lazy(() => {
    return import('./pages/AboutPage');
});
const ActivitiesPage = lazy(() => {
    return import('./pages/ActivitiesPage');
});
const BrandsPage = lazy(() => {
    return import('./pages/BrandsPage');
});
const CatalogPage = lazy(() => {
    return import('@/pages/CatalogPage').then((m) => {
        return { default: m.CatalogPage };
    });
});
const CompaniesPage = lazy(() => {
    return import('./pages/CompaniesPage');
});
const ContactPage = lazy(() => {
    return import('./pages/ContactPage');
});
const CountriesPage = lazy(() => {
    return import('./pages/CountriesPage');
});
const DiscountRulesPage = lazy(() => {
    return import('./pages/DiscountRulesPage');
});
const ExactConnectionPage = lazy(() => {
    return import('./pages/ExactConnectionPage');
});
const HomePage = lazy(() => {
    return import('@/pages/HomePage');
});
const MediaCataloguesPage = lazy(() => {
    return import('./pages/Media/MediaCataloguesPage');
});
const MediaImagesPage = lazy(() => {
    return import('./pages/Media/MediaImagesPage');
});
const MediaVideosPage = lazy(() => {
    return import('./pages/Media/MediaVideosPage');
});
const NotFound = lazy(() => {
    return import('./pages/NotFound');
});
const ProductionBatchesPage = lazy(() => {
    return import('./pages/ProductionBatchesPage');
});
const ProductionBatchPage = lazy(() => {
    return import('./pages/ProductionBatchPage');
});
const ProductPage = lazy(() => {
    return import('@/pages/ProductPage');
});
const ProfilePage = lazy(() => {
    return import('./pages/ProfilePage');
});
const ProvidersPage = lazy(() => {
    return import('./pages/ProvidersPage');
});
const QuotePage = lazy(() => {
    return import('./pages/QuotePage');
});
const QuotesPage = lazy(() => {
    return import('./pages/QuotesPage');
});
const QuoteSummaryPage = lazy(() => {
    return import('./pages/QuoteSummaryPage');
});
const SalesPage = lazy(() => {
    return import('./pages/SalesPage');
});
const SalesReportsPage = lazy(() => {
    return import('./pages/SalesReportsPage');
});
const SettingsPage = lazy(() => {
    return import('./pages/SettingsPage').then((m) => {
        return { default: m.SettingsPage };
    });
});
const ShippingLocationsPage = lazy(() => {
    return import('./pages/ShippingLocationsPage');
});
const StockAlarmsPage = lazy(() => {
    return import('./pages/StockAlarmsPage');
});
const StocksPage = lazy(() => {
    return import('./pages/StocksPage');
});
const StockMutationsPage = lazy(() => {
    return import('./pages/StockMutationsPage');
});
const StockValuationPage = lazy(() => {
    return import('./pages/StockValuationPage');
});
const UsersPage = lazy(() => {
    return import('./pages/UsersPage');
});

export default function Routes() {
    if (auth.isAnonymous()) {
        return (
            <DomRoutes>
                <Route element={ <LoginPage /> } path='*' />
                <Route element={ <LoginPage /> } path='/login' />
                <Route element={ <ConfirmLoginPage /> } path='/confirm-login' />
                <Route element={ <ResetPasswordPage /> } path='/reset-password' />
                <Route element={ <RequestPasswordResetPage /> } path='/request-password-reset' />
                <Route element={ <RegisterPage /> } path='/register' />
            </DomRoutes>
        );
    }

    if (auth.isRefreshRequired() || auth.is2faDisabled()) {
        return (
            <DomRoutes>
                <Route element={ <RefreshSessionPage /> } path='/refresh-session' />
                <Route element={ <Google2faPage /> } path='/connect-2fa' />
            </DomRoutes>
        );
    }

    return (
        <Suspense fallback={ <Loader /> }>
            <DomRoutes>
                <Route element={ <AboutPage /> } path='/about' />
                <Route element={ <ActivitiesPage /> } path='/activities' />
                <Route element={ <BrandsPage /> } path='/brands' />
                <Route element={ <CatalogPage /> } path='/catalog' />
                <Route element={ <ContactPage /> } path='/contact' />
                <Route element={ <CompaniesPage /> } path='/companies' />
                <Route element={ <CountriesPage /> } path='/countries' />
                <Route element={ <DiscountRulesPage /> } path='/discount-rules' />
                <Route element={ <ExactConnectionPage /> } path='/exact-connection' />
                <Route element={ <ProductionBatchesPage /> } path='/production-batches' />
                <Route element={ <ProductionBatchPage /> } path='/production-batches/:uuid' />
                <Route element={ <ProvidersPage /> } path='/providers' />
                <Route element={ <ShippingLocationsPage /> } path='/shipping-locations' />
                <Route element={ <UsersPage /> } path='/users' />
                <Route element={ <HomePage /> } path='/' />
                <Route element={ <ProductPage /> } path='/product/:uuid' />
                <Route element={ <ProfilePage /> } path='/profile' />
                <Route element={ <QuotesPage /> } path='/quotes' />
                <Route element={ <QuotePage /> } path='/quotes/:quoteId' />
                <Route element={ <SettingsPage /> } path='/settings' />
                <Route element={ <StocksPage /> } path='/stocks' />
                <Route element={ <StockAlarmsPage /> } path='/stock-alarms' />
                <Route element={ <StockValuationPage /> } path='/analysis/stock-valuation' />
                <Route element={ <StockMutationsPage /> } path='/analysis/stock-mutations' />
                <Route element={ <SalesPage /> } path='/analysis/outgoing-products' />
                <Route element={ <QuoteSummaryPage /> } path='/analysis/quote-summary' />
                <Route element={ <SalesReportsPage /> } path='/analysis/reports' />
                <Route element={ <MediaCataloguesPage /> } path='/media/catalogues' />
                <Route element={ <MediaImagesPage /> } path='/media/images' />
                <Route element={ <MediaVideosPage /> } path='/media/video' />
                {/* <Route element={ <MediaLogosPage /> } path='/media/logos' /> */}
                <Route element={ <Navigate replace to='/' /> } path='/login' />
                <Route element={ <Navigate replace to='/' /> } path='/confirm-login' />
                <Route element={ <Navigate replace to='/' /> } path='/register' />
                <Route element={ <Navigate replace to='/' /> } path='/reset-password' />
                <Route element={ <Navigate replace to='/' /> } path='/request-password-reset' />
                <Route element={ <NotFound /> } path='*' />
            </DomRoutes>
        </Suspense>
    );
}
